// In-memory access-token holder (§10 / P0-13). The short-lived access JWT
// lives ONLY here — a module-scoped variable, never `localStorage`/
// `sessionStorage`/a cookie readable by JS — so an XSS foothold can't lift a
// long-lived credential out of storage. It is intentionally lost on a full
// page reload; the boot sequence (`../boot`) re-establishes a session from
// the HttpOnly refresh cookie via one silent `POST /v1/auth/refresh`.
//
// Kept in its own tiny module (not on the Pinia auth store) so the API
// client (`./api`) can read the bearer token without importing the store,
// and the store can write it without importing the client — no import cycle,
// and both are trivially unit-testable in isolation.

let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function clearAccessToken(): void {
  accessToken = null;
}

// ─── Session-seen marker (PERF-03) ─────────────────────────────────────────
// A tiny, non-sensitive breadcrumb — NOT a credential, just "this browser has
// successfully established a session before" — persisted in localStorage so
// `initSession()` (../stores/auth.ts) can skip its boot-time `GET /v1/auth/me`
// + `POST /v1/auth/refresh` probe entirely for a browser that has never had a
// session. Those calls legitimately 401 for a first-time/anonymous visitor,
// which the browser's network layer logs to the console as a resource-load
// failure regardless of how the JS promise is handled (can't be silenced with
// try/catch) — this is exactly what Lighthouse's `errors-in-console` audit
// flags on a fresh visit to `/` or `/login`. Skipping the doomed-to-401 calls
// removes the noise without changing behaviour for anyone who HAS signed in
// before: their probe still runs, still validates against the server, which
// remains the sole authority. If storage is unavailable (private mode,
// blocked, quota), fail open to the original always-probe behaviour.
const SESSION_SEEN_KEY = 'eskay:session-seen';

export function hadSessionBefore(): boolean {
  try {
    return window.localStorage.getItem(SESSION_SEEN_KEY) === '1';
  } catch {
    return true;
  }
}

export function markSessionSeen(): void {
  try {
    window.localStorage.setItem(SESSION_SEEN_KEY, '1');
  } catch {
    // Storage unavailable — nothing to do; hadSessionBefore() fails open.
  }
}

export function clearSessionSeen(): void {
  try {
    window.localStorage.removeItem(SESSION_SEEN_KEY);
  } catch {
    // Storage unavailable — nothing to do.
  }
}
